/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-no-target-blank */
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useRef } from 'react';
import { ScaleLoader } from 'react-spinners';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/_lib/FormControl/_lib/ClickableTab';
import ProfileTab from '@/ui/_lib/links/ProfileTab';
import AnalyticsTab from '@/ui/_lib/links/AnalyticsTab';
import Container from '@/ui/_lib/Container';
import useAuth from '@/hooks/useAuth';
import Template1 from '@/ui/_lib/templates/Template1';
import Template2 from '@/ui/_lib/templates/Template2';
import Template3 from '@/ui/_lib/templates/Template3';
import Button from '@/ui/_lib/Button';
import { useUser } from '@/contexts/UserContext/UserContext';
import { UserService } from '@/ui/_apiUrls';
// Import template images
import template1 from '@/assets/image/templateone.png';
import template2 from '@/assets/image/templatetwo.png';
import template3 from '@/assets/image/templatethree.png';

function Links() {
  const { useAuthenticatedPage } = useAuth();
  const { user, updateUser, isLoading } = useUser();
  const [isChangeTemplateOpen, setIsChangeTemplateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<number>(user?.template ? Number(user.template) : 1);
  const navigate = useNavigate();
  useAuthenticatedPage();
  const [searchParams] = useSearchParams();
  
  // Add ref to track if we've already set the profile setup flag
  const hasSetProfileSetupFlag = useRef(false);
  
  // Add ref to track navigation attempts to prevent loops
  const navigationAttemptCount = useRef(0);

  const { mutate: patchUser } = UserService.patch({
    mutationParams: {
      onSuccess: (response) => {
        updateUser({ template: response.result.template });
        setIsChangeTemplateOpen(false);
      },
      onError: (error) => {
        // Using console.error is acceptable for error logging in development
        // eslint-disable-next-line no-console

      },
    },
  });

  const handleTemplateChange = (templateNumber: number) => {
    setSelectedTemplate(templateNumber);
    patchUser({ template: templateNumber });
  };

  // Show loading state while user data is being fetched
  if (isLoading || !user) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <ScaleLoader color="#039BE5" />
      </div>
    );
  }
  const isProfileComplete = user.title && user.profileComplete;
  const hasTemplate = user.template !== null && user.template !== undefined;

  // If user hasn't completed all setup steps, redirect them to the appropriate setup page
  // Prevent infinite navigation loops
  if (navigationAttemptCount.current < 5) {
    if (!isProfileComplete || !hasTemplate) {
      // Check what step they're missing
      if (!user.title) {
        navigationAttemptCount.current += 1;
        navigate('/profile-title');
        return null;
      }
      if (!user.profileComplete) {
        navigationAttemptCount.current += 1;
        navigate('/complete-profile');
        return null;
      }
      if (!hasTemplate) {
        // They have title and completed profile but no template
        navigationAttemptCount.current += 1;
        navigate('/choose-template');
        return null;
      }
    }
  }
  const hasCompletedProfileSetup = localStorage.getItem('hasCompletedProfileSetup');
  if (!hasSetProfileSetupFlag.current && !hasCompletedProfileSetup && isProfileComplete && hasTemplate) {
    localStorage.setItem('hasCompletedProfileSetup', 'true');
    hasSetProfileSetupFlag.current = true;
  }

  // Use the user's saved template, or fallback to URL parameter, or default to '1'
  const template = user?.template?.toString() || searchParams.get('template') || '1';

  const triggerClassName =
    'inline-flex items-center text-gray-500 border-b-[2px] border-b-primary-50 justify-center whitespace-nowrap px-3 py-2 text-[14px] font-semibold font-inter leading-[100%] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm data-[state=active]:border-b-[#039BE5] data-[state=active]:text-[#039BE5] h-full w-full';

  const renderTemplate = () => {
    switch (template) {
      case '1':
        return <Template1 />;
      case '2':
        return <Template2 />;
      case '3':
        return <Template3 />;
      default:
        return <Template1 />;
    }
  };

  // Function to get the appropriate template image
  const getTemplateImage = (templateNumber: number) => {
    switch (templateNumber) {
      case 1:
        return template1;
      case 2:
        return template2;
      case 3:
        return template3;
      default:
        return template1;
    }
  };

  return (
    <Container className="relative h-full">
      <Tabs defaultValue="template" className="flex  h-full w-full flex-col items-center justify-between">
        <TabsList className="flex w-full">
          <TabsTrigger value="template" className={triggerClassName}>
            Template
          </TabsTrigger>
          <TabsTrigger value="analytics" className={triggerClassName}>
            Analytics
          </TabsTrigger>
          <TabsTrigger value="profile" className={triggerClassName}>
            Profile
          </TabsTrigger>
        </TabsList>

        <TabsContent value="template" className="flex h-full w-full flex-col pt-2">
          <div className="flex-1">{renderTemplate()}</div>
          <div className="px-1 pb-4 md:px-8 md:pb-6">
            <Button
              type="button"
              variant="secondary"
              className="w-full py-2 md:py-3 md:text-lg md:max-w-md md:mx-auto md:block"
              onClick={() => setIsChangeTemplateOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setIsChangeTemplateOpen(true);
                }
              }}
              tabIndex={0}
              role="button"
            >
              Change Template
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="flex h-full w-full flex-col pt-2">
          <div className="flex-1">
            <AnalyticsTab />
          </div>
        </TabsContent>

        <TabsContent value="profile" className="flex h-full w-full flex-col pt-2">
          <div className="flex-1">
            <ProfileTab />
          </div>
        </TabsContent>
      </Tabs>

      {/* Change Template Modal */}
      {isChangeTemplateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Choose Template</h3>
              <button
                onClick={() => setIsChangeTemplateOpen(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
                type="button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="relative mx-auto mb-6 w-full max-w-[200px]">
              {/* Template images - displayed directly */}
              <div className="relative mx-auto w-full">
                {/* All three template images with visibility controlled by selectedTemplate state */}
                <img
                  src={template1}
                  alt="Template Preview 1"
                  className={`w-full rounded-xl ${selectedTemplate === 1 ? 'block' : 'hidden'}`}
                />
                <img
                  src={template2}
                  alt="Template Preview 2"
                  className={`w-full rounded-xl ${selectedTemplate === 2 ? 'block' : 'hidden'}`}
                />
                <img
                  src={template3}
                  alt="Template Preview 3"
                  className={`w-full rounded-xl ${selectedTemplate === 3 ? 'block' : 'hidden'}`}
                />
              </div>

              {/* Template navigation dots */}
              <div className="mt-4 flex justify-center space-x-2">
                {[1, 2, 3].map((templateNumber) => (
                  <button
                    key={templateNumber}
                    onClick={() => setSelectedTemplate(templateNumber)}
                    className={`h-2 w-2 rounded-full ${selectedTemplate === templateNumber ? 'bg-[#039BE5]' : 'bg-gray-300'}`}
                    aria-label={`Template ${templateNumber}`}
                    type="button"
                  />
                ))}
              </div>
            </div>

            <div className="mb-6 grid grid-cols-3 gap-2">
              {[1, 2, 3].map((templateNumber) => (
                <button
                  key={templateNumber}
                  onClick={() => setSelectedTemplate(templateNumber)}
                  className={`flex flex-col items-center rounded-lg border p-2 ${
                    selectedTemplate === templateNumber
                      ? 'border-[#039BE5] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  type="button"
                >
                  <img
                    src={getTemplateImage(templateNumber)}
                    alt={`Template ${templateNumber}`}
                    className="mb-1 h-16 w-16 object-contain"
                  />
                  <span className="text-xs">Template {templateNumber}</span>
                </button>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setIsChangeTemplateOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => handleTemplateChange(selectedTemplate)}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}

export default Links;